import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { FileText, Printer, Plus, Trash2, Settings as SettingsIcon, Building2, Search, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => n == null ? '-' : 'R ' + Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({ sku: '', description: '', qty: 1, unit_price: 0, discount_percent: 0 });
const emptyInvoice = () => ({
  doc_type: 'invoice',
  status: 'due',
  issue_date: todayISO(),
  due_date: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  customer_address: '',
  vat_inclusive: false,
  vat_rate_percent: 0,
  notes: '',
  line_items: [emptyLine()],
});

export default function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchText, setSearchText] = useState('');

  const [parts, setParts] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyInvoice());
  const [saving, setSaving] = useState(false);
  const [partPickerRow, setPartPickerRow] = useState(null);
  const [partSearch, setPartSearch] = useState('');

  const [companyOpen, setCompanyOpen] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [company, setCompany] = useState({
    company_name: '', trading_as: '', registration_no: '', address: '',
    phone: '', email: '', vat_no: '', bank_name: '', bank_account: '',
    bank_branch: '', vat_rate_percent: 15,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ---------- fetchers ----------
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.doc_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await axios.get(`${API_URL}/api/customer-invoices`, { headers, params });
      setInvoices(res.data || []);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterStatus]);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts-catalog?limit=500`, { headers });
      setParts(res.data || []);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompany = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoice-settings`, { headers });
      setCompany(res.data);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchParts(); fetchCompany(); }, [fetchParts, fetchCompany]);

  // ---------- totals preview ----------
  const computedTotals = useMemo(() => {
    let grand = 0;
    (form.line_items || []).forEach(li => {
      const net = (Number(li.qty) || 0) * (Number(li.unit_price) || 0) * (1 - (Number(li.discount_percent) || 0) / 100);
      grand += net;
    });
    return { grand };
  }, [form]);

  // ---------- invoice actions ----------
  const openNewInvoice = (preset = null) => {
    setForm(preset ? { ...emptyInvoice(), ...preset } : emptyInvoice());
    setDialogOpen(true);
  };

  const setLine = (idx, patch) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, line_items: items });
  };

  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (idx) => {
    if (form.line_items.length === 1) return;
    setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
  };

  const openPartPicker = (idx) => { setPartPickerRow(idx); setPartSearch(''); };
  const applyPartPick = (p) => {
    if (partPickerRow == null) return;
    setLine(partPickerRow, {
      sku: p.sku || '',
      description: p.name || '',
      unit_price: Number(p.sale_price) || 0,
    });
    setPartPickerRow(null);
    setPartSearch('');
  };

  const partCandidates = useMemo(() => {
    const q = (partSearch || '').trim().toLowerCase();
    if (!q) return parts.slice(0, 30);
    return parts.filter(p =>
      (p.sku || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    ).slice(0, 40);
  }, [partSearch, parts]);

  const saveInvoice = async () => {
    if (!(form.line_items || []).length) {
      toast.error('Add at least one line item');
      return;
    }
    for (const li of form.line_items) {
      if (!(li.description || '').trim()) { toast.error('Every line needs a description'); return; }
      if (!(Number(li.qty) > 0)) { toast.error('Every line needs qty > 0'); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form, vat_rate_percent: Number(form.vat_rate_percent) || 0 };
      payload.line_items = form.line_items.map(li => ({
        sku: (li.sku || '').toUpperCase(),
        description: li.description,
        qty: Number(li.qty) || 0,
        unit_price: Number(li.unit_price) || 0,
        discount_percent: Number(li.discount_percent) || 0,
      }));
      const res = await axios.post(`${API_URL}/api/customer-invoices`, payload, { headers });
      toast.success(`Created ${res.data.invoice_number}`);
      setDialogOpen(false);
      await fetchInvoices();
      // Auto-download the PDF
      await downloadPdf(res.data.id, res.data.invoice_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (id, number) => {
    try {
      const res = await axios.get(`${API_URL}/api/customer-invoices/${id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const printPdf = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/customer-invoices/${id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url);
      if (w) setTimeout(() => w.print(), 600);
    } catch {
      toast.error('Print failed');
    }
  };

  const toggleStatus = async (inv) => {
    const next = inv.status === 'paid' ? 'due' : 'paid';
    try {
      await axios.put(`${API_URL}/api/customer-invoices/${inv.id}`, { status: next }, { headers });
      toast.success(`Marked ${next.toUpperCase()}`);
      await fetchInvoices();
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteInvoice = async (id, number) => {
    if (!window.confirm(`Delete ${number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/customer-invoices/${id}`, { headers });
      toast.success('Deleted');
      await fetchInvoices();
    } catch {
      toast.error('Delete failed');
    }
  };

  const saveCompany = async () => {
    setCompanySaving(true);
    try {
      const payload = { ...company, vat_rate_percent: Number(company.vat_rate_percent) || 15 };
      const res = await axios.put(`${API_URL}/api/invoice-settings`, payload, { headers });
      setCompany(res.data);
      toast.success('Company details saved');
      setCompanyOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setCompanySaving(false);
    }
  };

  // ---------- filters ----------
  const filteredInvoices = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter(i =>
      (i.invoice_number || '').toLowerCase().includes(q) ||
      (i.customer_name || '').toLowerCase().includes(q) ||
      (i.customer_phone || '').toLowerCase().includes(q)
    );
  }, [invoices, searchText]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-400" /> Customer Invoices &amp; Quotations
          </h2>
          <p className="text-slate-400 text-sm">Generate professional PDF invoices. Auto-numbered and saved.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCompanyOpen(true)}
            className="bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
            data-testid="invoice-company-settings-btn"
          >
            <Building2 className="h-4 w-4 mr-1" /> Company Details
          </Button>
          <Button
            size="sm"
            onClick={() => openNewInvoice()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="new-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-slate-400">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Invoice #, customer name, phone..."
                className="pl-9 bg-slate-900 border-slate-700 text-white"
                data-testid="invoice-search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="invoice">Invoices</SelectItem>
                <SelectItem value="quotation">Quotations</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="due">Due</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-slate-400 text-center">Loading...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8 text-slate-400 text-center">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p>No invoices yet. Click "New Invoice" to create your first one.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Number</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400 text-right">Total</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map(inv => (
                  <TableRow key={inv.id} className="border-slate-700/60 hover:bg-slate-800/40" data-testid={`invoice-row-${inv.invoice_number}`}>
                    <TableCell className="font-mono text-amber-300">
                      {inv.invoice_number}
                      {inv.doc_type === 'quotation' && (
                        <Badge className="ml-2 bg-indigo-900 border border-indigo-700 text-indigo-200 text-[10px]">QUOTE</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">{inv.issue_date}</TableCell>
                    <TableCell className="text-white">
                      {inv.customer_name || <span className="text-slate-500 italic">(cash sale)</span>}
                      {inv.customer_phone ? <span className="text-slate-500 text-xs block">{inv.customer_phone}</span> : null}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-400">{R(inv.grand_total)}</TableCell>
                    <TableCell>
                      <Badge className={
                        inv.status === 'paid'
                          ? 'bg-emerald-950 border border-emerald-700 text-emerald-300'
                          : inv.status === 'draft'
                          ? 'bg-slate-800 border border-slate-600 text-slate-300'
                          : 'bg-rose-950 border border-rose-700 text-rose-300'
                      }>
                        {inv.status === 'paid' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        {(inv.status || '').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => printPdf(inv.id)}
                        className="h-7 bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
                        data-testid={`print-invoice-${inv.invoice_number}`}>
                        <Printer className="h-3 w-3 mr-1" /> Print
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(inv.id, inv.invoice_number)}
                        className="h-7 bg-slate-900 border-slate-700 text-white hover:bg-slate-700"
                        data-testid={`download-invoice-${inv.invoice_number}`}>
                        PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(inv)}
                        className="h-7 bg-slate-900 border-slate-700 text-emerald-300 hover:bg-slate-700"
                        data-testid={`toggle-status-${inv.invoice_number}`}>
                        {inv.status === 'paid' ? 'Mark Due' : 'Mark Paid'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv.id, inv.invoice_number)}
                        className="h-7 text-rose-400 hover:text-rose-300"
                        data-testid={`delete-invoice-${inv.invoice_number}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoice builder dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-400" /> New {form.doc_type === 'invoice' ? 'Invoice' : 'Quotation'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Document Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="quotation">Quotation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Issue Date</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" data-testid="issue-date-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="due">Due</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Customer Name</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" placeholder="Cash Sale / John Doe"
                data-testid="customer-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Phone</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" placeholder="0821234567"
                data-testid="customer-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Email (optional)</Label>
              <Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Address (optional)</Label>
              <Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" />
            </div>
          </div>

          {/* Line items */}
          <div className="border border-slate-700 rounded-md">
            <div className="bg-slate-800 grid grid-cols-12 gap-2 px-3 py-2 text-xs text-slate-400 uppercase">
              <div className="col-span-2">SKU</div>
              <div className="col-span-5">Description</div>
              <div className="col-span-1 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit Price</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1"></div>
            </div>
            {form.line_items.map((li, idx) => {
              const lineTotal = (Number(li.qty) || 0) * (Number(li.unit_price) || 0) * (1 - (Number(li.discount_percent) || 0) / 100);
              return (
                <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800 items-center" data-testid={`line-item-${idx}`}>
                  <div className="col-span-2 flex gap-1">
                    <Input value={li.sku} onChange={(e) => setLine(idx, { sku: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white text-xs font-mono h-8" placeholder="SKU" />
                    <Button size="sm" variant="outline" onClick={() => openPartPicker(idx)} className="bg-slate-900 border-slate-700 h-8 px-2 text-xs" data-testid={`pick-part-${idx}`}>
                      Pick
                    </Button>
                  </div>
                  <div className="col-span-5">
                    <Input value={li.description} onChange={(e) => setLine(idx, { description: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white text-sm h-8" placeholder="Part or service" />
                  </div>
                  <div className="col-span-1">
                    <Input type="number" step="0.5" min="0" value={li.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white h-8 text-right text-sm" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" min="0" value={li.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white h-8 text-right text-sm" />
                  </div>
                  <div className="col-span-1 text-right text-emerald-400 font-semibold text-sm">{R(lineTotal)}</div>
                  <div className="col-span-1 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} disabled={form.line_items.length === 1}
                      className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300" data-testid={`remove-line-${idx}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className="px-3 py-2 border-t border-slate-800">
              <Button size="sm" variant="outline" onClick={addLine}
                className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="add-line-btn">
                <Plus className="h-3 w-3 mr-1" /> Add line
              </Button>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} className="bg-slate-800 border-slate-700 text-white" placeholder="Any additional notes for the customer..." />
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-4 flex items-center justify-between">
              <span className="text-white font-bold uppercase tracking-wide">Total Due</span>
              <span className="text-amber-300 text-2xl font-bold">{R(computedTotals.grand)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="invoice-cancel-btn">
              Cancel
            </Button>
            <Button onClick={saveInvoice} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-invoice-btn">
              {saving ? 'Saving...' : 'Save & Print PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Part picker dialog */}
      <Dialog open={partPickerRow != null} onOpenChange={(o) => { if (!o) setPartPickerRow(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xl" data-testid="part-picker-dialog">
          <DialogHeader><DialogTitle>Select catalog part</DialogTitle></DialogHeader>
          <Input autoFocus value={partSearch} onChange={(e) => setPartSearch(e.target.value)}
            placeholder="Search SKU, name or category..." className="bg-slate-800 border-slate-700 text-white"
            data-testid="part-picker-search" />
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-800 border border-slate-700 rounded">
            {partCandidates.length === 0 ? (
              <div className="p-4 text-slate-500 text-center text-sm">No matching parts</div>
            ) : partCandidates.map(p => (
              <button key={p.id} onClick={() => applyPartPick(p)}
                className="w-full text-left px-3 py-2 hover:bg-slate-800 flex items-center justify-between gap-2"
                data-testid={`picker-part-${p.sku}`}>
                <div className="min-w-0">
                  <div className="font-mono text-xs text-indigo-300">{p.sku}</div>
                  <div className="text-sm text-white truncate">{p.name}</div>
                </div>
                <div className="text-emerald-400 font-semibold text-sm whitespace-nowrap">{R(p.sale_price)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Company details dialog */}
      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5 text-amber-400" /> Invoice Company Details
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400">These appear on every invoice/quotation PDF.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['company_name', 'Legal Company Name'],
              ['trading_as', 'Trading As'],
              ['registration_no', 'Registration No'],
              ['vat_no', 'VAT No (optional)'],
              ['phone', 'Phone'],
              ['email', 'Email'],
              ['address', 'Address'],
              ['bank_name', 'Bank Name'],
              ['bank_account', 'Bank Account'],
              ['bank_branch', 'Branch Code (optional)'],
            ].map(([key, label]) => (
              <div key={key} className={key === 'address' ? 'md:col-span-2' : ''}>
                <Label className="text-xs text-slate-400">{label}</Label>
                <Input value={company[key] || ''} onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" data-testid={`company-${key}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyOpen(false)}
              className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</Button>
            <Button onClick={saveCompany} disabled={companySaving}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-company-btn">
              {companySaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
